<script setup lang="ts" generic="Item extends object = any">
import type { HeadlessDialog } from "#components";
import { Controller, Keyboard, Zoom } from "swiper/modules";
import type { Swiper } from "swiper/types";
import type { HTMLAttributes } from "vue";

const emit = defineEmits<{
  "update:modelValue": [value: Item[keyof Item]];
  "update:isOpen": [value: boolean];
  zoomChange: [value: any];
}>();

const props = withDefaults(
  defineProps<{
    isOpen?: boolean;

    class?: HTMLAttributes["class"];

    items?: Item[];

    itemKey?: string | ((item: Item) => any);
    modelValue?: any;

    hideDetails?: boolean;
  }>(),
  { items: (): Item[] => [], itemKey: "id" }
);

const getItemKeyValue = (item: Item): any =>
  typeof props.itemKey
    ? typeof props.itemKey === "function"
      ? props.itemKey(item)
      : props.itemKey in item && (item as any)[props.itemKey]
    : item;

const currentItem = useVModel(props, "modelValue", emit, {
  passive: true,
  defaultValue: undefined,
});

const isOpen = useVModel(props, "isOpen", emit, {
  passive: true,
  defaultValue: false,
});

const fullSwiper = ref<Swiper>();

const { width } = useUserDevice();

watch(width, () => {
  nextTick(() => {
    fullSwiper.value?.update();
  });
});

const slideToCurrentItem = () => {
  const index = props.items.findIndex((item, index) => {
    const value = getItemKeyValue(item);
    return value ? value === currentItem.value : index === currentItem.value;
  });

  fullSwiper.value?.slideTo(index, 0);
};

const { ignoreUpdates } = watchIgnorable(currentItem, slideToCurrentItem);

const setItem = (index?: number) => {
  ignoreUpdates(() => {
    const value = getItemKeyValue(props.items[index!]);
    currentItem.value = value ? value : index;
  });
};

whenever(
  () => !isOpen.value,
  () => {
    ignoreUpdates(() => {
      currentItem.value = undefined;
    });
  }
);

const dialog = ref<typeof HeadlessDialog>();

const slide = (dir: "prev" | "next") => {
  if (isOpen.value) {
    if (dir === "prev") {
      fullSwiper.value?.slidePrev();
    } else {
      fullSwiper.value?.slideNext();
    }
  }
};

const onScroll = (event: WheelEvent) => {
  event.preventDefault();
  const { deltaY } = event;

  if (Math.abs(deltaY) < 10) return;

  if (deltaY > 0) {
    fullSwiper.value?.zoom.in();
  } else {
    fullSwiper.value?.zoom.out();
  }
};

const zoomRecalc = () => {
  nextTick(() => {
    fullSwiper.value?.zoom.in(0.01);

    nextTick(() => {
      fullSwiper.value?.zoom.out();

      nextTick(() => {
        fullSwiper.value?.update();
      });
    });
  });
};

const onItemLoad = () => {
  zoomRecalc();
};

defineExpose({
  fullSwiper,
  // miniSwiper,
});
</script>

<template>
  <Teleport to="body">
    <HeadlessTransitionRoot :show="isOpen" as="template">
      <HeadlessDialog
        class="app-lightbox relative z-40 text-sm pt-[4.125rem] lg:pt-0 select-none"
        @close="isOpen = false"
        :class="props.class"
        v-bind="$attrs"
        ref="dialog"
      >
        <HeadlessTransitionChild as="template">
          <div
            class="app-dialog__overlay fixed inset-0 bg-black/80 backdrop-blur-sm cursor-pointer"
            aria-hidden="true"
          ></div>
        </HeadlessTransitionChild>

        <HeadlessTransitionChild as="template">
          <HeadlessDialogPanel>
            <div class="fixed inset-0 w-screen h-screen">
              <div
                class="absolute top-5 z-10 end-5 lg:top-10 lg:end-10"
                v-if="!hideDetails"
              >
                <AppBtn
                  :icon="true"
                  variant="social"
                  @click="isOpen = false"
                  title="Zamknij"
                >
                  <Icon name="ns:close-big" class="block lg:hidden w-4 h-4" />
                  <Icon name="ns:close" class="hidden lg:block" />
                </AppBtn>
              </div>

              <div
                class="flex flex-col w-screen h-screen items-stretch justify-stretch gap-6"
              >
                <div
                  class="grow min-h-0 lg:mt-6 lg:px-[7rem]"
                  :class="{ 'lg:mb-6': !$slots['item-mini'] }"
                >
                  <div class="relative h-full">
                    <Swiper
                      :modules="[Controller, Keyboard, Zoom]"
                      :initial-slide="currentItem"
                      :zoom="true"
                      :keyboard="{ enabled: true }"
                      @swiper="
                        fullSwiper = $event;
                        zoomRecalc();
                      "
                      class="!absolute inset-0 w-full h-full"
                      @slide-change="setItem($event.activeIndex)"
                      @zoom-change="$emit('zoomChange', $event)"
                    >
                      <SwiperSlide
                        v-for="(item, index) in items"
                        class="max-h-full"
                        @wheel="onScroll"
                      >
                        <slot
                          name="item-full"
                          v-bind="{ props, item, index, onItemLoad }"
                        >
                          {{ item }}
                        </slot>
                      </SwiperSlide>
                    </Swiper>
                  </div>
                </div>

                <div
                  v-if="!hideDetails"
                  class="absolute z-10 top-1/2 w-full flex items-center justify-between px-5 lg:px-10"
                >
                  <AppBtn
                    :icon="true"
                    variant="social"
                    @click="slide('prev')"
                    class="absolute start-5 lg:start-10"
                    :class="{
                      'opacity-0 pointer-events-none': fullSwiper?.isBeginning,
                    }"
                  >
                    <svg
                      width="18"
                      height="12"
                      viewBox="0 0 18 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-4 h-4"
                    >
                      <path
                        d="M0.861271 5.74701C0.568378 6.03991 0.568378 6.51478 0.861271 6.80767L5.63424 11.5806C5.92713 11.8735 6.40201 11.8735 6.6949 11.5806C6.9878 11.2878 6.9878 10.8129 6.6949 10.52L2.45226 6.27734L6.6949 2.0347C6.9878 1.74181 6.9878 1.26694 6.6949 0.974042C6.40201 0.681149 5.92714 0.681148 5.63424 0.974042L0.861271 5.74701ZM17.3916 5.52734L1.3916 5.52734L1.3916 7.02734L17.3916 7.02734L17.3916 5.52734Z"
                        fill="currentColor"
                      />
                    </svg>
                  </AppBtn>

                  <AppBtn
                    :icon="true"
                    variant="social"
                    @click="slide('next')"
                    class="absolute end-5 lg:end-10"
                    :class="{
                      'opacity-0 pointer-events-none': fullSwiper?.isEnd,
                    }"
                  >
                    <svg
                      width="18"
                      height="12"
                      viewBox="0 0 18 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      class="w-4 h-4"
                    >
                      <path
                        d="M16.9219 6.80767C17.2148 6.51478 17.2148 6.03991 16.9219 5.74701L12.149 0.974043C11.8561 0.681149 11.3812 0.681149 11.0883 0.974043C10.7954 1.26694 10.7954 1.74181 11.0883 2.0347L15.3309 6.27734L11.0883 10.52C10.7954 10.8129 10.7954 11.2878 11.0883 11.5806C11.3812 11.8735 11.8561 11.8735 12.149 11.5806L16.9219 6.80767ZM0.391602 7.02734L16.3916 7.02734V5.52734L0.391602 5.52734L0.391602 7.02734Z"
                        fill="currentColor"
                      />
                    </svg>
                  </AppBtn>
                </div>
              </div>
            </div>
          </HeadlessDialogPanel>
        </HeadlessTransitionChild>
      </HeadlessDialog>
    </HeadlessTransitionRoot>
  </Teleport>
</template>
